import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowLeft,
  BadgeCheck,
  Bell,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  ClipboardList,
  ClipboardCheck,
  Clock,
  DollarSign,
  Euro,
  ExternalLink,
  FileCheck,
  FileText,
  Filter,
  FolderClock,
  Handshake,
  Hourglass,
  Inbox,
  LayoutDashboard,
  Link2,
  Loader2,
  MessageSquare,
  NotebookPen,
  PackageOpen,
  Phone,
  Plus,
  Receipt,
  Rocket,
  Save,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Ticket,
  Trash2,
  Truck,
  User as UserIcon,
  Users,
  X,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AdminDemoProvider, useAdminDemo } from "../context/AdminDemoContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { formatEUR, countryFlag } from "../lib/format";
import { toast } from "sonner";
import AdminTransports from "./admin/AdminTransports";
import StockFeedPanel from "../components/StockFeedPanel";
import StockFeedStatusCard from "../components/StockFeedStatusCard";

function formatApiError(e) {
  const d = e?.response?.data;
  if (!d) return e?.message || "Errore";
  if (typeof d === "string") return d;
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail)) {
    return d.detail
      .map((it) => it?.msg || (typeof it === "string" ? it : JSON.stringify(it)))
      .join("; ");
  }
  return e?.message || "Errore";
}

const NAV = [
  { to: "", key: "overview", Icon: LayoutDashboard, end: true },
  { to: "inbox", key: "inbox", Icon: Inbox },
  { to: "pratiche", key: "pratiche", Icon: ClipboardList },
  { to: "dealers", key: "dealers", Icon: Building2 },
  { to: "stock-feeds", key: "stock_feeds", Icon: Link2 },
  { to: "users", key: "users", Icon: Users },
  { to: "vehicles", key: "vehicles", Icon: Truck },
  { to: "commissions", key: "commissions", Icon: Euro },
  { to: "transports", key: "transports", Icon: Truck },
  { to: "activity", key: "activity", Icon: Activity },
  { to: "roadmap", key: "roadmap", Icon: Rocket },
];

const STATUS_LABELS_KEY = "admin.status";
const URGENCY_LABEL_KEY = "admin.urgency";

const STEP_PILL_STYLE = {
  richiesta_ricevuta: "bg-slate-100 text-slate-700 border-slate-200",
  verifica_in_corso: "bg-sky-50 text-sky-700 border-sky-200",
  trattativa: "bg-indigo-50 text-indigo-700 border-indigo-200",
  accordo_raggiunto: "bg-violet-50 text-violet-700 border-violet-200",
  documenti: "bg-amber-50 text-amber-700 border-amber-200",
  pagamento: "bg-orange-50 text-orange-700 border-orange-200",
  trasporto_consegna: "bg-blue-50 text-blue-700 border-blue-200",
  conclusa: "bg-emerald-50 text-emerald-700 border-emerald-200",
  annullata: "bg-red-50 text-red-700 border-red-200",
};

const URGENCY_PILL = {
  alta: "bg-red-50 text-red-700 border-red-200",
  normale: "bg-slate-100 text-slate-600 border-slate-200",
  bassa: "bg-slate-50 text-slate-500 border-slate-200",
};

const ADMIN_STATES = [
  "richiesta_ricevuta",
  "presa_in_carico",
  "contatto_venditore",
  "disponibilita_verificata",
  "prezzo_confermato",
  "trattativa_in_corso",
  "accordo_raggiunto",
  "documenti_richiesti",
  "documenti_verificati",
  "pagamento_autorizzato",
  "pagamento_ricevuto",
  "trasporto_organizzato",
  "veicolo_ritirato",
  "veicolo_consegnato",
  "pratica_conclusa",
  "annullata",
];

export default function AdminApp() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/home" replace />;

  return (
    <AdminDemoProvider>
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 py-6 md:py-10" data-testid="admin-app">
        <AdminSidebar />
        <div className="min-w-0">
          <AdminDemoToggle />
          <AdminErrorBoundary>
            <Routes>
              <Route index element={<AdminOverview />} />
              <Route path="inbox" element={<AdminInbox />} />
              <Route path="pratiche" element={<AdminPraticheList />} />
              <Route path="pratiche/:id" element={<AdminPraticaDetail />} />
              <Route path="dealers" element={<AdminDealers />} />
              <Route path="stock-feeds" element={<AdminStockFeeds />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="transports" element={<AdminTransports />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="roadmap" element={<AdminRoadmap />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </AdminErrorBoundary>
        </div>
      </div>
    </AdminDemoProvider>
  );
}

/**
 * Global "Mostra dati demo" toggle. Sits above every admin section
 * and drives the `include_demo` query parameter on every admin API
 * request via `useAdminDemo().demoParams()`. Default: OFF (real data
 * only). Session-persistent through `AdminDemoProvider`.
 */
function AdminDemoToggle() {
  const { t } = useTranslation();
  const { includeDemo, setIncludeDemo } = useAdminDemo();
  return (
    <div
      data-testid="admin-demo-toggle-bar"
      className={`mb-5 rounded-2xl border px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap ${
        includeDemo
          ? "border-amber-200 bg-amber-50"
          : "border-slate-100 bg-white"
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
          {t("admin.demo_toggle_label")}
          {includeDemo && (
            <span
              data-testid="admin-demo-toggle-badge"
              className="text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white rounded-full px-2 py-0.5"
            >
              {t("admin.demo_chip")}
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 leading-snug max-w-2xl mt-0.5">
          {includeDemo
            ? t("admin.demo_toggle_active_hint")
            : t("admin.demo_toggle_hint")}
        </div>
      </div>
      <Switch
        checked={includeDemo}
        onCheckedChange={setIncludeDemo}
        data-testid="admin-demo-toggle-switch"
      />
    </div>
  );
}

/**
 * Defensive error boundary around the admin content area. Without this,
 * an uncaught exception in any inner component silently unmounts the
 * subtree and the page appears blank on production builds (React 18
 * dev overlay would show the error, but prod ships a blank frame).
 */
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Admin content crashed:", error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div
          data-testid="admin-error-boundary"
          className="rounded-2xl bg-red-50 border border-red-200 text-red-800 p-6"
        >
          <div className="font-heading font-semibold text-base mb-1">
            Impossibile caricare questa sezione
          </div>
          <div className="text-sm opacity-90">
            {String(this.state.error?.message || this.state.error)}
          </div>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-red-200 px-4 h-9 text-xs font-semibold hover:bg-red-100 transition-colors"
          >
            Riprova
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Resolve a sidebar/nav slug to an absolute admin path. Kept as a
 * helper because React Router v6 evaluates relative `to` values against
 * the CURRENT location, so `<NavLink to="pratiche">` at `/admin/inbox`
 * would navigate to `/admin/inbox/pratiche` — an unmatched path that
 * renders blank. Absolute paths eliminate the accumulation bug. */
function adminPath(slug) {
  const s = (slug || "").replace(/^\/+/, "");
  return s ? `/admin/${s}` : "/admin";
}

function AdminSidebar() {
  const { t } = useTranslation();
  return (
    <aside className="lg:sticky lg:top-24 self-start rounded-2xl bg-white border border-slate-100 p-3">
      <div className="px-3 pb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
          {t("admin.title")}
        </div>
      </div>
      <ul className="flex flex-col gap-0.5">
        {NAV.map(({ to, key, Icon, end }) => (
          <li key={key}>
            <NavLink
              end={end}
              to={adminPath(to)}
              data-testid={`admin-nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t(`admin.nav.${key}`)}
            </NavLink>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// ─────────────────────────── OVERVIEW (operational cockpit) ──────────────
function AdminOverview() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/admin/dashboard", { params: demoParams() })
      .then((r) => setData(r.data))
      .catch(() => setData({}));
  }, [demoParams]);

  if (!data) return <SpinnerRow />;

  const queues = data.queues || {};
  const totals = data.totals || {};
  const commissions = data.commissions || {};

  const TILES = [
    { key: "new_requests", Icon: FolderClock, tone: "info", queue: "new_requests" },
    { key: "open_negotiations", Icon: Handshake, tone: "info", queue: "open_negotiations" },
    { key: "urgent", Icon: ShieldAlert, tone: "danger", queue: "urgent" },
    { key: "docs_missing", Icon: FileText, tone: "warning", queue: "docs_missing" },
    { key: "payments_pending", Icon: DollarSign, tone: "warning", queue: "payments_pending" },
    { key: "transports_to_assign", Icon: Truck, tone: "info", queue: "transports_to_assign" },
    { key: "vehicles_in_transit", Icon: Truck, tone: "info", queue: "vehicles_in_transit" },
    { key: "deliveries_expected", Icon: Check, tone: "success", queue: "deliveries_expected" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {t("admin.overview_overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter">
          {t("admin.overview_title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("admin.overview_subtitle")}</p>
      </div>

      {/* Queue tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {TILES.map(({ key, Icon, tone, queue }) => (
          <QueueTile
            key={key}
            testid={`queue-tile-${key}`}
            title={t(`admin.queue.${key}`)}
            value={queues[key] ?? 0}
            Icon={Icon}
            tone={tone}
            to={`/admin/pratiche?queue=${queue}`}
          />
        ))}
      </div>

      {/* Commissions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          testid="stat-commissions-month"
          title={t("admin.commissions_month")}
          value={formatEUR(commissions.month_eur || 0)}
          hint={t("admin.commissions_month_hint")}
          Icon={Euro}
          highlight
        />
        <StatCard
          testid="stat-commissions-all"
          title={t("admin.commissions_all")}
          value={formatEUR(commissions.all_time_eur || 0)}
          hint={t("admin.commissions_all_hint")}
          Icon={Receipt}
        />
        <StatCard
          testid="stat-total-pratiche"
          title={t("admin.total_pratiche")}
          value={totals.pratiche ?? 0}
          hint={t("admin.total_pratiche_hint")}
          Icon={ClipboardList}
        />
      </div>

      {/* Urgent preview */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-heading font-semibold text-lg tracking-tight">
            {t("admin.urgent_preview_title")}
          </h2>
          <Link to="/admin/pratiche?queue=urgent" className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1">
            {t("admin.view_queue")} <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        {(data.urgent_preview || []).length === 0 ? (
          <EmptyBox text={t("admin.no_urgent")} />
        ) : (
          <ul className="space-y-2" data-testid="urgent-preview-list">
            {data.urgent_preview.map((p) => (
              <li key={p.id}>
                <PraticaRow pratica={p} to={`/admin/pratiche/${p.id}`} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function QueueTile({ testid, title, value, Icon, tone = "info", to }) {
  const toneMap = {
    info: "bg-blue-600/10 text-blue-700",
    warning: "bg-amber-500/10 text-amber-700",
    danger: "bg-red-500/10 text-red-700",
    success: "bg-emerald-500/10 text-emerald-700",
  };
  const valueTone = {
    info: "text-blue-700",
    warning: "text-amber-700",
    danger: "text-red-600",
    success: "text-emerald-700",
  };
  return (
    <Link to={to} data-testid={testid}>
      <motion.div
        whileHover={{ y: -2 }}
        className="rounded-2xl bg-white border border-slate-100 p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneMap[tone] || toneMap.info}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <div className={`mt-3 font-heading font-bold text-3xl tabular-nums ${valueTone[tone] || ""}`}>{value}</div>
        <div className="mt-0.5 text-xs text-slate-500 leading-snug">{title}</div>
      </motion.div>
    </Link>
  );
}

function StatCard({ testid, title, value, hint, Icon, highlight = false }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl p-5 border shadow-sm ${
        highlight ? "bg-slate-950 border-slate-900 text-white" : "bg-white border-slate-100"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${highlight ? "text-blue-300" : "text-slate-400"}`} />
        <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${highlight ? "text-slate-300" : "text-slate-400"}`}>
          {title}
        </div>
      </div>
      <div className={`mt-2 font-heading font-bold text-2xl tabular-nums ${highlight ? "text-white" : "text-slate-950"}`}>
        {value}
      </div>
      {hint && <div className={`mt-1 text-xs ${highlight ? "text-slate-400" : "text-slate-500"}`}>{hint}</div>}
    </div>
  );
}

// ─────────────────────────── PRATICHE LIST ─────────────────────────────
function AdminPraticheList() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [urgency, setUrgency] = useState("");
  const [country, setCountry] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = searchParams.get("queue") || "";

  const load = () => {
    const params = { limit: 200, ...demoParams() };
    if (q) params.q = q;
    if (status) params.status = status;
    if (urgency) params.urgency = urgency;
    if (country === "IT" || country === "DE") params.country_to = country;
    if (queue) params.queue = queue;
    api.get("/admin/pratiche", { params }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, status, urgency, country, queue, demoParams]);

  return (
    <div className="space-y-6" data-testid="admin-pratiche-list">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("admin.pratiche_overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter">
            {t("admin.pratiche_title")}
          </h1>
        </div>
        {queue && (
          <button
            type="button"
            onClick={() => setSearchParams({})}
            data-testid="clear-queue"
            className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1 hover:text-slate-900"
          >
            <X className="w-3 h-3" />
            {t("admin.clear_queue", { queue: t(`admin.queue.${queue}`) })}
          </button>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 p-4 flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="pratiche-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.search_placeholder")}
            className="pl-8 h-10"
          />
        </div>
        <SelectFilter
          testid="filter-status"
          value={status}
          onChange={setStatus}
          placeholder={t("admin.filter.status")}
          options={ADMIN_STATES.map((s) => ({ value: s, label: t(`admin.state.${s}`) }))}
        />
        <SelectFilter
          testid="filter-urgency"
          value={urgency}
          onChange={setUrgency}
          placeholder={t("admin.filter.urgency")}
          options={[
            { value: "alta", label: t("admin.urgency.alta") },
            { value: "normale", label: t("admin.urgency.normale") },
            { value: "bassa", label: t("admin.urgency.bassa") },
          ]}
        />
        <SelectFilter
          testid="filter-country"
          value={country}
          onChange={setCountry}
          placeholder={t("admin.filter.country")}
          options={[
            { value: "IT", label: "🇮🇹 IT" },
            { value: "DE", label: "🇩🇪 DE" },
          ]}
        />
      </div>

      <div className="text-xs text-slate-500" data-testid="pratiche-total">
        {t("admin.result_count", { count: total })}
      </div>

      {items === null ? (
        <SpinnerRow />
      ) : items.length === 0 ? (
        <EmptyBox text={t("admin.no_pratiche")} />
      ) : (
        <ul className="space-y-2" data-testid="admin-pratiche-items">
          {items.map((p) => (
            <li key={p.id}>
              <PraticaRow pratica={p} to={`/admin/pratiche/${p.id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SelectFilter({ testid, value, onChange, options, placeholder }) {
  const finalOpts = [{ value: "__all__", label: placeholder }, ...options];
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger className="h-10 w-auto min-w-[140px]" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {finalOpts.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PraticaRow({ pratica, to }) {
  const { t } = useTranslation();
  const v = pratica.vehicle;
  const dealerStatus = pratica.dealer_status || "richiesta_ricevuta";
  const urgencyKey = pratica.urgency || "normale";
  const price = pratica.negotiated_price || pratica.requested_price || v?.price_eur || 0;
  const seller = pratica.seller_dealer;
  const verification = seller?.verification || {};
  return (
    <Link
      to={to}
      data-testid={`admin-pratica-row-${pratica.id}`}
      className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 p-3 hover:shadow-md transition-shadow"
    >
      <div className="w-14 h-10 rounded-md bg-slate-100 shrink-0 overflow-hidden">
        {v?.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-sm truncate">
            {v ? `${v.brand} ${v.model}` : "—"}
          </span>
          <span className="text-[10px] font-mono text-slate-500">{pratica.code}</span>
          {verification.suspended && (
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5">
              {t("admin.dealer_suspended")}
            </span>
          )}
          {verification.new_partner && (
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
              {t("admin.dealer_new")}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-500 truncate">
          {t(`admin.state.${pratica.status}`)} · {countryFlag(pratica.country_from)} → {countryFlag(pratica.country_to)} · {pratica.responsabile || "—"}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-heading font-bold text-sm tabular-nums">{formatEUR(price)}</div>
        <div className="mt-0.5 flex items-center gap-1 justify-end">
          <span className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 rounded px-1.5 py-0.5 border ${STEP_PILL_STYLE[dealerStatus]}`}>
            {t(`pratiche.step.${dealerStatus}`)}
          </span>
          {urgencyKey && (
            <span className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center rounded px-1.5 py-0.5 border ${URGENCY_PILL[urgencyKey]}`}>
              {t(`admin.urgency.${urgencyKey}`)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────── PRATICA DETAIL ────────────────────────────
function AdminPraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pratica, setPratica] = useState(null);
  const [tab, setTab] = useState("control");

  const load = () => {
    api.get(`/admin/pratiche/${id}`).then((r) => setPratica(r.data)).catch(() => setPratica(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!pratica) return <SpinnerRow />;
  const v = pratica.vehicle;
  const seller = pratica.seller_dealer;
  const verif = seller?.verification || {};

  return (
    <div className="space-y-6" data-testid={`admin-pratica-detail-${pratica.id}`}>
      <button
        type="button"
        onClick={() => navigate("/admin/pratiche")}
        data-testid="admin-pratica-back"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("admin.back_to_list")}
      </button>

      {/* Header */}
      <div className="rounded-2xl bg-[#0A0A0B] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
              {pratica.code} · {t(`pratiche.type.${pratica.type}`)}
            </div>
            <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter">
              {v ? `${v.brand} ${v.model}` : t("pratiche.title")}
            </h1>
            <p className="text-slate-300 text-sm mt-1">
              {v?.trim} · {v?.year} · {countryFlag(pratica.country_from)} {pratica.country_from} → {countryFlag(pratica.country_to)} {pratica.country_to}
            </p>
            {seller && (
              <p className="text-slate-400 text-xs mt-2 inline-flex items-center gap-2 flex-wrap">
                {t("admin.seller")}: <strong className="text-slate-200 font-semibold">{seller.name}</strong>
                {verif.verified && <span className="inline-flex items-center gap-0.5 bg-blue-500/15 text-blue-300 px-1.5 py-0.5 rounded text-[10px]"><ShieldCheck className="w-3 h-3" /> {t("admin.verified")}</span>}
                {verif.new_partner && <span className="inline-flex items-center gap-0.5 bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded text-[10px]">{t("admin.dealer_new")}</span>}
                {verif.suspended && <span className="inline-flex items-center gap-0.5 bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded text-[10px]">{t("admin.dealer_suspended")}</span>}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {t(`admin.state.${pratica.status}`)}
            </div>
            <div className="mt-1 font-heading font-bold text-2xl tabular-nums">
              {formatEUR(pratica.negotiated_price || pratica.requested_price || v?.price_eur || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {["control", "docs", "notes", "reminders", "contacts", "payments", "transport", "chat", "activity"].map((k) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === k ? "text-blue-600 border-blue-600" : "text-slate-500 border-transparent hover:text-slate-900"
            }`}
          >
            {t(`admin.pt_tab.${k}`)}
          </button>
        ))}
      </div>

      {tab === "control" && <ControlPanel pratica={pratica} onChange={load} />}
      {tab === "docs" && <DocumentsPanel pratica={pratica} onChange={load} />}
      {tab === "notes" && <InternalNotesPanel pratica={pratica} onChange={load} />}
      {tab === "reminders" && <RemindersPanel pratica={pratica} onChange={load} />}
      {tab === "contacts" && <ContactLogsPanel pratica={pratica} onChange={load} />}
      {tab === "payments" && <PaymentsPanel pratica={pratica} onChange={load} />}
      {tab === "transport" && <TransportPanel pratica={pratica} onChange={load} />}
      {tab === "chat" && <ChatPanel pratica={pratica} onChange={load} />}
      {tab === "activity" && <ActivityPanel pratica={pratica} />}
    </div>
  );
}

// ─────────────────────────── Control panel (status + commission + fields) ─
function ControlPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(pratica.status);
  const [statusNote, setStatusNote] = useState("");
  const [negotiated, setNegotiated] = useState(pratica.negotiated_price ?? "");
  const [quantity, setQuantity] = useState(pratica.quantity ?? 1);
  const [urgency, setUrgency] = useState(pratica.urgency || "normale");
  const [responsabile, setResponsabile] = useState(pratica.responsabile || "Team AUTEXIO");
  const [nextDeadline, setNextDeadline] = useState(pratica.next_deadline?.slice(0, 10) || "");
  const [transportCost, setTransportCost] = useState(pratica.transport_cost ?? "");
  const [saving, setSaving] = useState(false);

  // Commission
  const [commTotal, setCommTotal] = useState(pratica.commission?.total_eur ?? 0);
  const [buyerShare, setBuyerShare] = useState(pratica.commission?.buyer_share_eur ?? 0);
  const [sellerShare, setSellerShare] = useState(pratica.commission?.seller_share_eur ?? 0);
  const [invStatus, setInvStatus] = useState(pratica.commission_invoice_status || "not_issued");
  const [payStatus, setPayStatus] = useState(pratica.commission_payment_status || "pending");
  const [savingComm, setSavingComm] = useState(false);

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pratiche/${pratica.id}/status`, { status, note: statusNote || null });
      setStatusNote("");
      toast.success(t("admin.status_saved"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const savePatch = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pratiche/${pratica.id}`, {
        negotiated_price: negotiated === "" ? null : parseFloat(negotiated),
        quantity: parseInt(quantity) || 1,
        urgency,
        responsabile,
        next_deadline: nextDeadline ? new Date(nextDeadline).toISOString() : null,
        transport_cost: transportCost === "" ? null : parseFloat(transportCost),
      });
      toast.success(t("admin.saved"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const saveCommission = async () => {
    setSavingComm(true);
    try {
      await api.patch(`/admin/pratiche/${pratica.id}/commission`, {
        total_eur: parseFloat(commTotal),
        buyer_share_eur: parseFloat(buyerShare),
        seller_share_eur: parseFloat(sellerShare),
        invoice_status: invStatus,
        payment_status: payStatus,
      });
      toast.success(t("admin.commission_saved"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSavingComm(false);
    }
  };

  // Auto-adjust seller share when total or buyer share changes.
  useEffect(() => {
    const t = parseFloat(commTotal || 0);
    const b = parseFloat(buyerShare || 0);
    if (!isNaN(t) && !isNaN(b)) setSellerShare(Math.max(0, +(t - b).toFixed(2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commTotal, buyerShare]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Status editor */}
      <Panel title={t("admin.control.status_title")} testid="admin-control-status">
        <Label className="text-xs font-semibold text-slate-500">{t("admin.control.status")}</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="mt-1" data-testid="admin-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ADMIN_STATES.map((s) => (
              <SelectItem key={s} value={s}>{t(`admin.state.${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="mt-3">
          <Label className="text-xs font-semibold text-slate-500">{t("admin.control.status_note")}</Label>
          <Textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} data-testid="admin-status-note" className="mt-1" />
        </div>
        <Button
          onClick={saveStatus}
          disabled={saving || status === pratica.status}
          data-testid="admin-status-save"
          className="mt-3 rounded-full bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          {t("admin.control.apply_status")}
        </Button>
      </Panel>

      {/* Negotiation fields */}
      <Panel title={t("admin.control.negotiation_title")} testid="admin-control-negotiation">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("pratiche.negotiated")} (€)</Label>
            <Input value={negotiated} onChange={(e) => setNegotiated(e.target.value)} type="number" data-testid="admin-negotiated-price" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("trattativa.quantity")}</Label>
            <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" min="1" data-testid="admin-quantity" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("trattativa.urgency")}</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bassa">{t("admin.urgency.bassa")}</SelectItem>
                <SelectItem value="normale">{t("admin.urgency.normale")}</SelectItem>
                <SelectItem value="alta">{t("admin.urgency.alta")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.deadline")}</Label>
            <Input type="date" value={nextDeadline} onChange={(e) => setNextDeadline(e.target.value)} data-testid="admin-deadline" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("pratiche.responsabile")}</Label>
            <Select value={responsabile} onValueChange={setResponsabile}>
              <SelectTrigger className="mt-1" data-testid="admin-responsabile"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Alessandro (Founder)">Alessandro (Founder)</SelectItem>
                <SelectItem value="Marco Bianchi">Marco Bianchi</SelectItem>
                <SelectItem value="Elena Rossi">Elena Rossi</SelectItem>
                <SelectItem value="Team AUTEXIO">Team AUTEXIO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("pratiche.transport_cost")} (€)</Label>
            <Input value={transportCost} onChange={(e) => setTransportCost(e.target.value)} type="number" data-testid="admin-transport-cost" className="mt-1" />
          </div>
        </div>
        <Button
          onClick={savePatch}
          disabled={saving}
          data-testid="admin-negotiation-save"
          className="mt-3 rounded-full bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          {t("admin.saved_button")}
        </Button>
      </Panel>

      {/* Commission split */}
      <Panel title={t("admin.control.commission_title")} testid="admin-control-commission" span2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.total")}</Label>
            <Input value={commTotal} onChange={(e) => setCommTotal(e.target.value)} type="number" data-testid="admin-comm-total" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.buyer_share")}</Label>
            <Input value={buyerShare} onChange={(e) => setBuyerShare(e.target.value)} type="number" data-testid="admin-comm-buyer" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.seller_share")}</Label>
            <Input value={sellerShare} readOnly type="number" data-testid="admin-comm-seller" className="mt-1 bg-slate-50" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.invoice_status")}</Label>
            <Select value={invStatus} onValueChange={setInvStatus}>
              <SelectTrigger className="mt-1" data-testid="admin-comm-invoice"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="not_issued">{t("admin.inv.not_issued")}</SelectItem>
                <SelectItem value="issued">{t("admin.inv.issued")}</SelectItem>
                <SelectItem value="paid">{t("admin.inv.paid")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{t("admin.control.payment_status")}</Label>
            <Select value={payStatus} onValueChange={setPayStatus}>
              <SelectTrigger className="mt-1" data-testid="admin-comm-pay"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t("admin.pay.pending")}</SelectItem>
                <SelectItem value="paid">{t("admin.pay.paid")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={saveCommission}
              disabled={savingComm}
              data-testid="admin-comm-save"
              className="rounded-full bg-blue-600 hover:bg-blue-700 w-full"
            >
              {savingComm ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {t("admin.saved_button")}
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500 italic">{t("admin.control.commission_hint")}</p>
      </Panel>
    </div>
  );
}

// ─────────────────────────── Documents (admin toggle + upload placeholder) ─
const DOC_CHECKLIST = [
  { slug: "contratto", name: "Contratto di intermediazione AUTEXIO" },
  { slug: "fattura", name: "Fattura venditore" },
  { slug: "cmr", name: "CMR trasporto" },
  { slug: "coc", name: "Certificato di Conformità (COC)" },
  { slug: "libretto", name: "Libretto di circolazione" },
  { slug: "visura", name: "Visura camerale venditore" },
  { slug: "kaufvertrag", name: "Kaufvertrag" },
];

function DocumentsPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const availableSlugs = new Set((pratica.documents_available || []).map((d) => d.slug));
  const [busy, setBusy] = useState(null);

  const toggle = async (slug, name, next) => {
    setBusy(slug);
    try {
      await api.patch(`/admin/pratiche/${pratica.id}/documents`, { slug, name, available: next });
      onChange();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel title={t("admin.control.documents_title")} testid="admin-docs-panel">
      <ul className="space-y-2" data-testid="admin-docs-checklist">
        {DOC_CHECKLIST.map((d) => {
          const on = availableSlugs.has(d.slug);
          return (
            <li
              key={d.slug}
              className="flex items-center gap-3 rounded-xl border border-slate-100 p-3"
              data-testid={`admin-doc-row-${d.slug}`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${on ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-400"}`}>
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm truncate">{d.name}</div>
                <div className="text-xs text-slate-500">{on ? t("admin.doc.available") : t("admin.doc.missing")}</div>
              </div>
              <Switch
                checked={on}
                onCheckedChange={(v) => toggle(d.slug, d.name, v)}
                disabled={busy === d.slug}
                data-testid={`admin-doc-toggle-${d.slug}`}
              />
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-slate-500 italic">{t("admin.doc.upload_placeholder")}</p>
    </Panel>
  );
}

// ─────────────────────────── Internal notes ─────────────────────────────
function InternalNotesPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/pratiche/${pratica.id}/internal-notes`, { text });
      setText("");
      onChange();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title={t("admin.notes.title")} testid="admin-notes-panel">
      <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs mb-3">
        <NotebookPen className="w-3.5 h-3.5 inline-block mr-1" />
        {t("admin.notes.hint")}
      </div>
      <ul className="space-y-2 mb-4" data-testid="admin-notes-list">
        {(pratica.internal_notes || []).slice().reverse().map((n, i) => (
          <li key={i} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="mt-1 text-xs text-slate-500 tabular-nums">
              {n.author} · {new Date(n.date).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </li>
        ))}
      </ul>
      <form onSubmit={submit} className="flex items-end gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("admin.notes.placeholder")} data-testid="admin-notes-input" />
        <Button type="submit" disabled={saving} data-testid="admin-notes-submit" className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </form>
    </Panel>
  );
}

// ─────────────────────────── Reminders ─────────────────────────────────
function RemindersPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/pratiche/${pratica.id}/reminders`, { text, due_date: due || null });
      setText(""); setDue("");
      onChange();
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (rid, done) => {
    await api.patch(`/admin/pratiche/${pratica.id}/reminders/${rid}`, { done });
    onChange();
  };

  const remove = async (rid) => {
    await api.delete(`/admin/pratiche/${pratica.id}/reminders/${rid}`);
    onChange();
  };

  return (
    <Panel title={t("admin.reminders.title")} testid="admin-reminders-panel">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 mb-4">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("admin.reminders.placeholder")} data-testid="admin-reminder-input" />
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="admin-reminder-due" />
        <Button type="submit" disabled={saving} data-testid="admin-reminder-submit" className="rounded-full bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4" /> : <Plus className="w-4 h-4 mr-1" />} {t("admin.reminders.add")}
        </Button>
      </form>
      <ul className="space-y-2" data-testid="admin-reminders-list">
        {(pratica.reminders || []).map((r) => {
          const overdue = !r.done && r.due_date && new Date(r.due_date) < new Date();
          return (
            <li
              key={r.id}
              className={`flex items-center gap-3 rounded-xl border p-3 ${
                overdue ? "border-red-200 bg-red-50/60" : r.done ? "border-slate-100 bg-slate-50" : "border-slate-100 bg-white"
              }`}
              data-testid={`admin-reminder-row-${r.id}`}
            >
              <button type="button" onClick={() => toggle(r.id, !r.done)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${r.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-blue-500"}`}>
                <Check className="w-3.5 h-3.5" />
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${r.done ? "line-through text-slate-400" : "text-slate-800"}`}>{r.text}</div>
                <div className="text-xs text-slate-500 tabular-nums mt-0.5">
                  {r.due_date ? new Date(r.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : t("admin.reminders.no_due")}
                  {overdue && <span className="ml-2 text-red-600 font-semibold">{t("admin.reminders.overdue")}</span>}
                </div>
              </div>
              <button type="button" onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-500" data-testid={`admin-reminder-delete-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          );
        })}
        {(pratica.reminders || []).length === 0 && (
          <li className="text-sm text-slate-500 py-4 text-center">{t("admin.reminders.empty")}</li>
        )}
      </ul>
    </Panel>
  );
}

// ─────────────────────────── Contact logs ──────────────────────────────
function ContactLogsPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const [type, setType] = useState("call");
  const [direction, setDirection] = useState("out");
  const [contact, setContact] = useState("");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/pratiche/${pratica.id}/contact-logs`, { type, direction, contact: contact || null, summary });
      setSummary(""); setContact("");
      onChange();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title={t("admin.contacts.title")} testid="admin-contacts-panel">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[110px_110px_1fr_1fr_auto] gap-2 mb-4">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="admin-contact-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="call">{t("admin.contacts.call")}</SelectItem>
            <SelectItem value="email">{t("admin.contacts.email")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger data-testid="admin-contact-direction"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="out">{t("admin.contacts.out")}</SelectItem>
            <SelectItem value="in">{t("admin.contacts.in")}</SelectItem>
          </SelectContent>
        </Select>
        <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t("admin.contacts.contact_placeholder")} data-testid="admin-contact-name" />
        <Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder={t("admin.contacts.summary_placeholder")} data-testid="admin-contact-summary" />
        <Button type="submit" disabled={saving} data-testid="admin-contact-submit" className="rounded-full bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
      </form>
      <ul className="space-y-2" data-testid="admin-contact-logs">
        {(pratica.contact_logs || []).slice().reverse().map((c) => (
          <li key={c.id} className="rounded-xl border border-slate-100 p-3 flex items-start gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.type === "call" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"}`}>
              {c.type === "call" ? <Phone className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.summary}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {c.contact ? `${c.contact} · ` : ""}{t(`admin.contacts.${c.direction}`)} · {new Date(c.date).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {c.actor && <span> · {c.actor}</span>}
              </div>
            </div>
          </li>
        ))}
        {(pratica.contact_logs || []).length === 0 && (
          <li className="text-sm text-slate-500 py-4 text-center">{t("admin.contacts.empty")}</li>
        )}
      </ul>
    </Panel>
  );
}

// ─────────────────────────── Payments panel (protected structure — no real money) ───
function PaymentsPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const p = pratica.payments || {};
  const [form, setForm] = useState({
    provider: p.provider || "non_assegnato",
    transaction_id: p.transaction_id || "",
    buyer_kyc: p.buyer_kyc || "non_avviato",
    seller_kyc: p.seller_kyc || "non_avviato",
    funds_received: !!p.funds_received,
    funds_received_amount: p.funds_received_amount ?? "",
    funds_received_date: p.funds_received_date?.slice(0, 10) || "",
    funds_available: !!p.funds_available,
    funds_released_to_seller: !!p.funds_released_to_seller,
    funds_released_amount: p.funds_released_amount ?? "",
    funds_released_date: p.funds_released_date?.slice(0, 10) || "",
    autexio_commission_retained: !!p.autexio_commission_retained,
    carrier_payment: !!p.carrier_payment,
    carrier_payment_amount: p.carrier_payment_amount ?? "",
    dispute_flag: !!p.dispute_flag,
    dispute_notes: p.dispute_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pratiche/${pratica.id}/payments`, {
        ...form,
        funds_received_amount: form.funds_received_amount === "" ? null : parseFloat(form.funds_received_amount),
        funds_released_amount: form.funds_released_amount === "" ? null : parseFloat(form.funds_released_amount),
        carrier_payment_amount: form.carrier_payment_amount === "" ? null : parseFloat(form.carrier_payment_amount),
        funds_received_date: form.funds_received_date ? new Date(form.funds_received_date).toISOString() : null,
        funds_released_date: form.funds_released_date ? new Date(form.funds_released_date).toISOString() : null,
      });
      toast.success(t("admin.saved"));
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title={t("admin.payments.title")} testid="admin-payments-panel">
      <div className="rounded-xl bg-blue-50 border border-blue-200 text-blue-900 px-3 py-2 text-xs mb-4">
        {t("admin.payments.hint")}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.payments.provider")}</Label>
          <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
            <SelectTrigger className="mt-1" data-testid="payments-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="non_assegnato">{t("admin.payments.provider_none")}</SelectItem>
              <SelectItem value="mangopay">Mangopay</SelectItem>
              <SelectItem value="altro">{t("admin.payments.provider_other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.payments.tx_id")}</Label>
          <Input value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} data-testid="payments-tx-id" className="mt-1" />
        </div>
        <div />
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.payments.buyer_kyc")}</Label>
          <Select value={form.buyer_kyc} onValueChange={(v) => setForm({ ...form, buyer_kyc: v })}>
            <SelectTrigger className="mt-1" data-testid="payments-buyer-kyc"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="non_avviato">{t("admin.payments.kyc.non_avviato")}</SelectItem>
              <SelectItem value="in_corso">{t("admin.payments.kyc.in_corso")}</SelectItem>
              <SelectItem value="approvato">{t("admin.payments.kyc.approvato")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">{t("admin.payments.seller_kyc")}</Label>
          <Select value={form.seller_kyc} onValueChange={(v) => setForm({ ...form, seller_kyc: v })}>
            <SelectTrigger className="mt-1" data-testid="payments-seller-kyc"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="non_avviato">{t("admin.payments.kyc.non_avviato")}</SelectItem>
              <SelectItem value="in_corso">{t("admin.payments.kyc.in_corso")}</SelectItem>
              <SelectItem value="approvato">{t("admin.payments.kyc.approvato")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div />
      </div>

      <PaymentsSubSection title={t("admin.payments.funds_in_title")}>
        <BoolToggle testid="payments-funds-received" label={t("admin.payments.funds_received")} value={form.funds_received} onChange={(v) => setForm({ ...form, funds_received: v })} />
        <NumberBox testid="payments-funds-received-amount" label={t("admin.payments.amount")} value={form.funds_received_amount} onChange={(v) => setForm({ ...form, funds_received_amount: v })} />
        <DateBox testid="payments-funds-received-date" label={t("admin.payments.date")} value={form.funds_received_date} onChange={(v) => setForm({ ...form, funds_received_date: v })} />
        <BoolToggle testid="payments-funds-available" label={t("admin.payments.funds_available")} value={form.funds_available} onChange={(v) => setForm({ ...form, funds_available: v })} />
      </PaymentsSubSection>

      <PaymentsSubSection title={t("admin.payments.release_title")}>
        <BoolToggle testid="payments-funds-released" label={t("admin.payments.funds_released_to_seller")} value={form.funds_released_to_seller} onChange={(v) => setForm({ ...form, funds_released_to_seller: v })} />
        <NumberBox testid="payments-funds-released-amount" label={t("admin.payments.amount")} value={form.funds_released_amount} onChange={(v) => setForm({ ...form, funds_released_amount: v })} />
        <DateBox testid="payments-funds-released-date" label={t("admin.payments.date")} value={form.funds_released_date} onChange={(v) => setForm({ ...form, funds_released_date: v })} />
        <BoolToggle testid="payments-commission-retained" label={t("admin.payments.commission_retained")} value={form.autexio_commission_retained} onChange={(v) => setForm({ ...form, autexio_commission_retained: v })} />
      </PaymentsSubSection>

      <PaymentsSubSection title={t("admin.payments.carrier_title")}>
        <BoolToggle testid="payments-carrier-payment" label={t("admin.payments.carrier_payment")} value={form.carrier_payment} onChange={(v) => setForm({ ...form, carrier_payment: v })} />
        <NumberBox testid="payments-carrier-amount" label={t("admin.payments.amount")} value={form.carrier_payment_amount} onChange={(v) => setForm({ ...form, carrier_payment_amount: v })} />
      </PaymentsSubSection>

      <PaymentsSubSection title={t("admin.payments.dispute_title")}>
        <BoolToggle testid="payments-dispute" label={t("admin.payments.dispute_flag")} value={form.dispute_flag} onChange={(v) => setForm({ ...form, dispute_flag: v })} />
        <div className="col-span-full">
          <Label className="text-xs font-semibold text-slate-500">{t("admin.payments.dispute_notes")}</Label>
          <Textarea rows={2} value={form.dispute_notes} onChange={(e) => setForm({ ...form, dispute_notes: e.target.value })} data-testid="payments-dispute-notes" className="mt-1" />
        </div>
      </PaymentsSubSection>

      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="payments-save" className="rounded-full bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
          {t("admin.save")}
        </Button>
      </div>
    </Panel>
  );
}

function PaymentsSubSection({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}
function BoolToggle({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
      <div className="text-sm text-slate-800">{label}</div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
function NumberBox({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-500">{label}</Label>
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1" />
    </div>
  );
}
function DateBox({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-500">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1" />
    </div>
  );
}

// ─────────────────────────── Transport panel (pratica-scoped) ───
function TransportPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/admin/transport-requests", { params: { pratica_id: pratica.id } })
      .then((r) => setRequests(r.data || []));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pratica.id]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/transport-requests", { pratica_id: pratica.id });
      toast.success(t("admin.transport_panel.created"));
      onChange();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setCreating(false);
    }
  };

  const canCreate = pratica.transport_required && ADMIN_STATES.indexOf(pratica.status) >= ADMIN_STATES.indexOf("accordo_raggiunto");

  return (
    <Panel title={t("admin.transport_panel.title")} testid="admin-transport-panel">
      {!pratica.transport_required && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 text-slate-600 px-4 py-3 text-sm">
          {t("admin.transport_panel.not_required")}
        </div>
      )}
      {pratica.transport_required && !canCreate && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
          {t("admin.transport_panel.not_ready")}
        </div>
      )}
      {canCreate && (requests?.length || 0) === 0 && (
        <div className="rounded-xl bg-white border border-blue-100 p-5 text-center">
          <Truck className="w-6 h-6 text-blue-600 mx-auto" />
          <p className="mt-2 text-sm text-slate-600">{t("admin.transport_panel.empty")}</p>
          <Button
            onClick={create}
            disabled={creating}
            data-testid="admin-create-transport-request"
            className="mt-3 rounded-full bg-blue-600 hover:bg-blue-700"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
            {t("admin.transport_panel.create")}
          </Button>
        </div>
      )}
      {(requests?.length || 0) > 0 && (
        <ul className="space-y-2" data-testid="admin-transport-panel-list">
          {requests.map((r) => (
            <li key={r.id} className="rounded-xl border border-slate-100 p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Truck className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm">
                  {r.pickup_city} → {r.delivery_city}
                </div>
                <div className="text-xs text-slate-500">
                  {t(`admin.transport_status.${r.status}`)} · {r.quotes?.length || 0} {t("admin.transport_panel.quotes_count")}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/transports")}
                data-testid={`admin-transport-open-${r.id}`}
              >
                {t("admin.transport_panel.open")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function ChatPanel({ pratica, onChange }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/pratiche/${pratica.id}/messages`, { text });
      setText("");
      onChange();
    } finally {
      setSending(false);
    }
  };
  return (
    <Panel title={t("admin.chat.title")} testid="admin-chat-panel">
      <div className="rounded-xl bg-slate-50 border border-slate-100 min-h-[280px] max-h-[400px] overflow-y-auto p-3 space-y-2" data-testid="admin-chat-history">
        {(pratica.messages || []).map((m, i) => {
          const isAdmin = m.author === "autexio" || m.author === "system";
          return (
            <div key={i} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                isAdmin ? "bg-white border border-slate-100" : "bg-blue-600 text-white"
              }`}>
                <div>{m.text}</div>
                <div className={`text-[10px] mt-1 tabular-nums ${isAdmin ? "text-slate-400" : "text-blue-100"}`}>
                  {m.author === "autexio" ? "Team AUTEXIO" : m.author === "system" ? "AUTEXIO" : t("admin.chat.dealer")}
                  {" · "}
                  {new Date(m.ts).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="mt-3 flex items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("admin.chat.placeholder")} data-testid="admin-chat-input" />
        <Button type="submit" disabled={sending} data-testid="admin-chat-send" className="rounded-full bg-blue-600 hover:bg-blue-700 h-10 w-10 p-0">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </Panel>
  );
}

function ActivityPanel({ pratica }) {
  const { t } = useTranslation();
  const items = [...(pratica.activity_history || []), ...(pratica.status_history || []).map((s) => ({
    action: `status→${s.status}`, date: s.at, actor: "system", note: s.note,
  }))].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <Panel title={t("admin.activity.title")} testid="admin-activity-panel">
      <ul className="space-y-2" data-testid="admin-activity-list">
        {items.map((ev, i) => (
          <li key={i} className="flex gap-3 items-start rounded-xl border border-slate-100 p-3">
            <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800">{ev.action}</div>
              {ev.note && <div className="text-xs text-slate-600 mt-0.5">{ev.note}</div>}
              <div className="text-xs text-slate-400 tabular-nums mt-0.5">
                {new Date(ev.date).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {ev.actor}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ─────────────────────────── Dealers (verification) ─────────────────────
function AdminDealers() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState("dealers");
  const [associateFor, setAssociateFor] = useState(null); // pending user
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const params = demoParams();
    const [d, p] = await Promise.all([
      api.get("/admin/dealers", { params }).then((r) => r.data || []).catch(() => []),
      api.get("/admin/pending-users", { params }).then((r) => r.data || []).catch(() => []),
    ]);
    setItems(d);
    setPending(p);
  };
  useEffect(() => { load(); }, [demoParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (items === null) return <SpinnerRow />;

  return (
    <div className="space-y-6" data-testid="admin-dealers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("admin.dealers_overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tighter">{t("admin.dealers_title")}</h1>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="admin-dealer-new-btn"
          className="h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4"
        >
          <Plus className="w-4 h-4 mr-1" /> {t("admin.dealer_new_btn")}
        </Button>
      </div>

      <div className="inline-flex bg-slate-100 rounded-xl p-1" data-testid="admin-dealers-tabs">
        <button
          type="button"
          onClick={() => setTab("dealers")}
          data-testid="admin-dealers-tab-dealers"
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${tab === "dealers" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
        >
          {t("admin.dealers_title")} · {items.length}
        </button>
        <button
          type="button"
          onClick={() => setTab("pending")}
          data-testid="admin-dealers-tab-pending"
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${tab === "pending" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
        >
          {t("admin.pending_title")}
          {pending.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 min-w-[18px] h-[18px] text-[10px] px-1">
              {pending.length}
            </span>
          )}
        </button>
      </div>

      {tab === "dealers" ? (
        <ul className="space-y-2">
          {items.map((d) => (
            <DealerRow key={d.id || d._id} dealer={d} onChange={load} />
          ))}
        </ul>
      ) : (
        <ul className="space-y-2" data-testid="admin-pending-list">
          {pending.length === 0 ? (
            <li className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 p-6 text-center">
              {t("admin.pending_empty")}
            </li>
          ) : (
            pending.map((u) => (
              <PendingUserRow key={u.id} user={u} onAssociate={() => setAssociateFor(u)} onChange={load} />
            ))
          )}
        </ul>
      )}

      {associateFor && (
        <AssociateDialog
          user={associateFor}
          dealers={items}
          onClose={() => setAssociateFor(null)}
          onDone={() => { setAssociateFor(null); load(); }}
        />
      )}
      {showCreate && (
        <CreateDealerDialog onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function PendingUserRow({ user, onAssociate, onChange }) {
  const { t } = useTranslation();
  const [inviting, setInviting] = useState(false);
  const [marking, setMarking] = useState(false);
  const emailVerified = user.email_verified !== false; // default true = existing users

  const invite = async () => {
    setInviting(true);
    try {
      const r = await api.post("/admin/invitations", { email: user.email, name: user.name || null, related_id: user.id });
      toast.success(t("admin.invite_sent", { status: r.data.status }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const markVerified = async () => {
    setMarking(true);
    try {
      await api.post(`/admin/users/${user.id}/mark-email-verified`);
      toast.success(t("admin.email_marked_verified_ok"));
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarking(false);
    }
  };

  return (
    <li
      data-testid={`admin-pending-row-${user.id}`}
      className="rounded-2xl bg-white border border-slate-100 p-4 flex items-center gap-3 flex-wrap"
    >
      <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 border border-amber-100 flex items-center justify-center">
        <Hourglass className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-sm">{user.name || user.email}</span>
          <span className="text-[9px] font-semibold uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
            {t("admin.pending_state")}
          </span>
          {user.is_demo && (
            <span
              data-testid={`admin-pending-demo-chip-${user.id}`}
              title={t("admin.demo_chip_hint")}
              className="text-[9px] font-semibold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5"
            >
              {t("admin.demo_chip")}
            </span>
          )}
          <span
            data-testid={`admin-email-verified-badge-${user.id}`}
            title={emailVerified ? t("admin.email_verified_yes") : t("admin.email_verified_no")}
            className={`text-[9px] font-semibold uppercase rounded px-1.5 py-0.5 border inline-flex items-center gap-1 ${
              emailVerified
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            {emailVerified ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
            {emailVerified ? t("admin.email_verified_short_yes") : t("admin.email_verified_short_no")}
          </span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
          <span>{user.email}</span>
          {user.signup_company_name && <span>· {user.signup_company_name}</span>}
          {user.signup_phone && <span>· {user.signup_phone}</span>}
          <span>· {(user.created_at || "").slice(0, 10)}</span>
        </div>
      </div>
      {!emailVerified && (
        <Button
          onClick={markVerified}
          disabled={marking}
          variant="outline"
          data-testid={`admin-mark-verified-btn-${user.id}`}
          title={t("admin.email_verified_override_hint")}
          className="h-9 rounded-full text-xs font-semibold px-4"
        >
          {marking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><BadgeCheck className="w-3.5 h-3.5 mr-1" /> {t("admin.mark_email_verified")}</>)}
        </Button>
      )}
      <Button
        onClick={onAssociate}
        data-testid={`admin-associate-btn-${user.id}`}
        className="h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4"
      >
        <BadgeCheck className="w-3.5 h-3.5 mr-1" />
        {t("admin.activate_associate")}
      </Button>
      <Button
        onClick={invite}
        disabled={inviting}
        variant="outline"
        data-testid={`admin-invite-btn-${user.id}`}
        className="h-9 rounded-full text-xs font-semibold px-4"
      >
        <Send className="w-3.5 h-3.5 mr-1" /> {t("admin.invite_email")}
      </Button>
    </li>
  );
}

function AssociateDialog({ user, dealers, onClose, onDone }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [dealerRole, setDealerRole] = useState("owner");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [newDealer, setNewDealer] = useState({
    name: user.signup_company_name || "",
    country: "IT",
    city: "",
    address: "",
    phone: user.signup_phone || "",
    email: user.email || "",
  });

  const filtered = dealers.filter((d) =>
    !search || (d.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      let did = selectedId;
      if (mode === "new") {
        const r = await api.post("/admin/dealers", newDealer);
        did = r.data.id || r.data._id;
      }
      if (!did) {
        setErr(t("admin.associate_pick_dealer"));
        setBusy(false);
        return;
      }
      await api.post(`/admin/users/${user.id}/associate-dealer`, {
        dealer_id: did, dealer_role: dealerRole,
      });
      toast.success(t("admin.activate_associate_ok"));
      onDone();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-associate-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl border border-slate-100">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg">{t("admin.activate_associate_title")}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {user.name || user.email} · {user.email}
        </p>

        <div className="mt-5 inline-flex bg-slate-100 rounded-xl p-1">
          <button
            type="button"
            onClick={() => setMode("existing")}
            data-testid="admin-associate-mode-existing"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mode === "existing" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          >
            {t("admin.associate_existing")}
          </button>
          <button
            type="button"
            onClick={() => setMode("new")}
            data-testid="admin-associate-mode-new"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${mode === "new" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          >
            {t("admin.associate_create_new")}
          </button>
        </div>

        {mode === "existing" ? (
          <div className="mt-4 space-y-3">
            <Input
              placeholder={t("admin.associate_search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-associate-search"
              className="h-10 rounded-xl"
            />
            <div className="max-h-56 overflow-auto rounded-xl border border-slate-100" data-testid="admin-associate-list">
              {filtered.slice(0, 60).map((d) => (
                <button
                  type="button"
                  key={d.id || d._id}
                  onClick={() => setSelectedId(d.id || d._id)}
                  data-testid={`admin-associate-pick-${d.id || d._id}`}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 ${selectedId === (d.id || d._id) ? "bg-blue-50 text-blue-700" : "hover:bg-slate-50"}`}
                >
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-[11px] text-slate-500">{countryFlag(d.country)} {d.city || ""}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Input placeholder={t("admin.field_name")} value={newDealer.name} onChange={(e) => setNewDealer({ ...newDealer, name: e.target.value })} className="col-span-2 h-10 rounded-xl" data-testid="admin-newdealer-name" />
            <select value={newDealer.country} onChange={(e) => setNewDealer({ ...newDealer, country: e.target.value })} className="h-10 rounded-xl border border-slate-200 text-sm px-3" data-testid="admin-newdealer-country">
              <option value="IT">🇮🇹 IT</option>
              <option value="DE">🇩🇪 DE</option>
            </select>
            <Input placeholder={t("admin.field_city")} value={newDealer.city} onChange={(e) => setNewDealer({ ...newDealer, city: e.target.value })} className="h-10 rounded-xl" data-testid="admin-newdealer-city" />
            <Input placeholder={t("admin.field_address")} value={newDealer.address} onChange={(e) => setNewDealer({ ...newDealer, address: e.target.value })} className="col-span-2 h-10 rounded-xl" data-testid="admin-newdealer-address" />
            <Input placeholder={t("admin.field_phone")} value={newDealer.phone} onChange={(e) => setNewDealer({ ...newDealer, phone: e.target.value })} className="h-10 rounded-xl" data-testid="admin-newdealer-phone" />
            <Input placeholder={t("admin.field_email")} value={newDealer.email} onChange={(e) => setNewDealer({ ...newDealer, email: e.target.value })} className="h-10 rounded-xl" data-testid="admin-newdealer-email" />
          </div>
        )}

        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {t("admin.associate_role_label")}
          </label>
          <select
            value={dealerRole}
            onChange={(e) => setDealerRole(e.target.value)}
            data-testid="admin-associate-dealer-role"
            className="mt-1 w-full h-10 rounded-xl border border-slate-200 text-sm px-3"
          >
            <option value="owner">owner</option>
            <option value="sales">sales</option>
            <option value="operator">operator</option>
          </select>
        </div>

        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="admin-associate-confirm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.activate_associate")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateDealerDialog({ onClose, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", country: "IT", city: "", address: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/admin/dealers", form);
      toast.success(t("admin.dealer_created"));
      onDone();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-create-dealer-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl border border-slate-100">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg">{t("admin.dealer_new_btn")}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Input placeholder={t("admin.field_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2 h-10 rounded-xl" data-testid="admin-create-name" />
          <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-10 rounded-xl border border-slate-200 text-sm px-3" data-testid="admin-create-country">
            <option value="IT">🇮🇹 IT</option>
            <option value="DE">🇩🇪 DE</option>
          </select>
          <Input placeholder={t("admin.field_city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 rounded-xl" data-testid="admin-create-city" />
          <Input placeholder={t("admin.field_address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="col-span-2 h-10 rounded-xl" data-testid="admin-create-address" />
          <Input placeholder={t("admin.field_phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 rounded-xl" data-testid="admin-create-phone" />
          <Input placeholder={t("admin.field_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 rounded-xl" data-testid="admin-create-email" />
        </div>
        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.name.trim()}
            data-testid="admin-create-confirm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.create")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Unified Inbox ──────────────────────────
function AdminInbox() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ type: "", status: "", q: "" });

  const load = () => {
    const params = new URLSearchParams();
    if (filters.type) params.set("type", filters.type);
    if (filters.status) params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    params.set("limit", "80");
    Object.entries(demoParams()).forEach(([k, v]) => params.set(k, String(v)));
    api.get(`/admin/inbox?${params}`).then((r) => setData(r.data));
  };
  useEffect(load, [filters, demoParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (item, status) => {
    if (item.type === "pratica_request") {
      navigate(`/admin/pratiche/${item.id}`);
      return;
    }
    await api.patch(`/admin/requests/${item.id}`, { status });
    toast.success(t("admin.inbox_status_updated"));
    load();
  };

  const typeColor = {
    pratica_request: "bg-blue-50 text-blue-700 border-blue-200",
    private_lead: "bg-emerald-50 text-emerald-700 border-emerald-200",
    contact: "bg-slate-100 text-slate-700 border-slate-200",
    service_quote: "bg-amber-50 text-amber-700 border-amber-200",
    info_request: "bg-violet-50 text-violet-700 border-violet-200",
  };
  const statusColor = {
    nuovo: "bg-red-50 text-red-700 border-red-200",
    in_lavorazione: "bg-amber-50 text-amber-700 border-amber-200",
    chiuso: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  return (
    <div className="space-y-5" data-testid="admin-inbox-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{t("admin.inbox_overline")}</p>
        <h1 className="mt-1 font-heading font-bold text-3xl tracking-tighter">{t("admin.inbox_title")}</h1>
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-inbox-filters">
        <select value={filters.type} onChange={(e) => setFilters((s) => ({ ...s, type: e.target.value }))} className="h-10 rounded-xl border border-slate-200 text-sm px-3" data-testid="admin-inbox-filter-type">
          <option value="">{t("admin.inbox_type_all")}</option>
          <option value="pratica_request">{t("admin.inbox_type.pratica_request")}</option>
          <option value="private_lead">{t("admin.inbox_type.private_lead")}</option>
          <option value="contact">{t("admin.inbox_type.contact")}</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters((s) => ({ ...s, status: e.target.value }))} className="h-10 rounded-xl border border-slate-200 text-sm px-3" data-testid="admin-inbox-filter-status">
          <option value="">{t("admin.inbox_status_all")}</option>
          <option value="nuovo">{t("admin.inbox_status.nuovo")}</option>
          <option value="in_lavorazione">{t("admin.inbox_status.in_lavorazione")}</option>
          <option value="chiuso">{t("admin.inbox_status.chiuso")}</option>
        </select>
        <Input
          placeholder={t("admin.inbox_search_placeholder")}
          value={filters.q}
          onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
          data-testid="admin-inbox-search"
          className="h-10 rounded-xl"
        />
      </div>

      {!data ? (
        <SpinnerRow />
      ) : data.items.length === 0 ? (
        <div className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 p-6 text-center">
          {t("admin.inbox_empty")}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-inbox-list">
          {data.items.map((item) => (
            <li
              key={`${item.type}-${item.id}`}
              data-testid={`admin-inbox-row-${item.id}`}
              className="rounded-2xl bg-white border border-slate-100 p-4 flex items-center gap-3 flex-wrap"
            >
              <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-2 py-0.5 border ${typeColor[item.type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                {item.type_label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {item.code ? item.code : (item.name || item.contact || "—")}
                  {item.car_label && <span className="text-slate-500 font-normal"> · {item.car_label}</span>}
                </div>
                <div className="text-[11px] text-slate-500 truncate mt-0.5">
                  {item.contact && <span>{item.contact}</span>}
                  {item.excerpt && <span> · {item.excerpt}</span>}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {(item.created_at || "").replace("T", " ").slice(0, 16)}
                </div>
              </div>
              <span className={`text-[10px] font-semibold uppercase rounded px-2 py-0.5 border ${statusColor[item.status] || ""}`}>
                {t(`admin.inbox_status.${item.status}`, { defaultValue: item.status })}
              </span>
              {item.type === "pratica_request" ? (
                <Button size="sm" variant="outline" onClick={() => navigate(item.deep_link)} data-testid={`admin-inbox-open-${item.id}`} className="h-8 rounded-full text-xs">
                  {t("admin.inbox_open")}
                </Button>
              ) : (
                <select
                  value={item.status}
                  onChange={(e) => changeStatus(item, e.target.value)}
                  data-testid={`admin-inbox-status-${item.id}`}
                  className="h-8 rounded-full border border-slate-200 text-[11px] px-2 font-semibold"
                >
                  <option value="nuovo">{t("admin.inbox_status.nuovo")}</option>
                  <option value="in_lavorazione">{t("admin.inbox_status.in_lavorazione")}</option>
                  <option value="chiuso">{t("admin.inbox_status.chiuso")}</option>
                </select>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DealerRow({ dealer, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const v = dealer.verification || {};
  const badgeCount = [v.verified, v.documents_verified, v.bank_verified].filter(Boolean).length;

  const toggleField = async (field, next) => {
    await api.patch(`/admin/dealers/${dealer.id || dealer._id}/verification`, { [field]: next });
    onChange();
  };

  const invite = async (e) => {
    e.stopPropagation();
    if (!dealer.email) return;
    setInviting(true);
    try {
      const r = await api.post("/admin/invitations", {
        email: dealer.email,
        name: dealer.name || null,
        related_id: dealer.id || dealer._id,
      });
      toast.success(t("admin.invite_sent", { status: r.data.status }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  return (
    <li className="rounded-2xl bg-white border border-slate-100" data-testid={`admin-dealer-row-${dealer.id || dealer._id}`}>
      <div className="w-full flex items-center gap-3 p-4">
        <button type="button" onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-3 text-left min-w-0">
          <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold text-sm">{dealer.name}</span>
              {v.suspended && <span className="text-[9px] font-semibold uppercase bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5">{t("admin.dealer_suspended")}</span>}
              {v.new_partner && <span className="text-[9px] font-semibold uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">{t("admin.dealer_new")}</span>}
              {v.verified && !v.suspended && !v.new_partner && <span className="text-[9px] font-semibold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> {t("admin.verified")}</span>}
              {dealer.is_demo && (
                <span
                  data-testid={`admin-dealer-demo-chip-${dealer.id || dealer._id}`}
                  title={t("admin.demo_chip_hint")}
                  className="text-[9px] font-semibold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5"
                >
                  {t("admin.demo_chip")}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500">{countryFlag(dealer.country)} {dealer.city} · {badgeCount}/3 {t("admin.badges")}</div>
          </div>
        </button>
        {dealer.email && (
          <Button
            onClick={invite}
            disabled={inviting}
            variant="outline"
            data-testid={`admin-dealer-invite-${dealer.id || dealer._id}`}
            title={dealer.email}
            className="h-9 rounded-full text-xs font-semibold px-4 shrink-0"
          >
            {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> {t("admin.invite_email")}</>}
          </Button>
        )}
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`admin-dealer-verification-${dealer.id || dealer._id}`}>
          <VerifRow label={t("admin.verif.verified")} value={!!v.verified} onChange={(x) => toggleField("verified", x)} testid={`verif-verified-${dealer.id || dealer._id}`} />
          <VerifRow label={t("admin.verif.documents")} value={!!v.documents_verified} onChange={(x) => toggleField("documents_verified", x)} testid={`verif-docs-${dealer.id || dealer._id}`} />
          <VerifRow label={t("admin.verif.bank")} value={!!v.bank_verified} onChange={(x) => toggleField("bank_verified", x)} testid={`verif-bank-${dealer.id || dealer._id}`} />
          <VerifRow label={t("admin.verif.new_partner")} value={!!v.new_partner} onChange={(x) => toggleField("new_partner", x)} testid={`verif-new-${dealer.id || dealer._id}`} />
          <VerifRow label={t("admin.verif.suspended")} value={!!v.suspended} onChange={(x) => toggleField("suspended", x)} testid={`verif-suspended-${dealer.id || dealer._id}`} danger />
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600">
            <div className="grid grid-cols-1 gap-1">
              <div><strong>{t("admin.verif.vat")}:</strong> {v.vat_number || "—"}</div>
              <div><strong>{t("admin.verif.tax_id")}:</strong> {v.tax_id || "—"}</div>
              <div><strong>{t("admin.verif.trade_register")}:</strong> {v.trade_register || "—"}</div>
              <div><strong>IBAN:</strong> ···· ···· {v.iban_tail || "····"}</div>
              <div><strong>{t("admin.verif.referente")}:</strong> {v.referente || "—"}</div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function VerifRow({ label, value, onChange, testid, danger }) {
  return (
    <div className={`flex items-center justify-between rounded-xl border p-3 ${danger && value ? "border-red-200 bg-red-50/50" : "border-slate-100"}`}>
      <div className={`text-sm ${danger && value ? "text-red-700 font-semibold" : "text-slate-800"}`}>{label}</div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// ─────────────────────────── Users / Vehicles / Commissions / Activity ─
function AdminUsers() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [items, setItems] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [associateFor, setAssociateFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [showPurge, setShowPurge] = useState(false);

  const load = async () => {
    const params = demoParams();
    const [u, d] = await Promise.all([
      api.get("/admin/users", { params }).then((r) => r.data || []).catch(() => []),
      api.get("/admin/dealers", { params }).then((r) => r.data || []).catch(() => []),
    ]);
    setItems(u);
    setDealers(d);
  };

  useEffect(() => { load(); }, [demoParams]); // eslint-disable-line react-hooks/exhaustive-deps

  if (items === null) return <SpinnerRow />;

  const pending = items.filter((u) => !u.dealer_id);
  const active = items.filter((u) => !!u.dealer_id);

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">{t("admin.users_title")}</h1>
        <Button
          onClick={() => setShowPurge(true)}
          variant="outline"
          data-testid="admin-users-purge-btn"
          className="h-10 rounded-full text-xs font-semibold px-4 border-red-200 text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" /> {t("admin.purge_test_btn")}
        </Button>
      </div>

      {/* Pending users: prominent activation call-to-action */}
      {pending.length > 0 && (
        <section data-testid="admin-users-pending-section">
          <h2 className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold mb-2 flex items-center gap-2">
            <Hourglass className="w-3.5 h-3.5" /> {t("admin.pending_title")}
            <span className="inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 min-w-[18px] h-[18px] text-[10px] px-1">
              {pending.length}
            </span>
          </h2>
          <ul className="space-y-2" data-testid="admin-users-pending-list">
            {pending.map((u) => (
              <UserRow
                key={u.id || u._id}
                user={u}
                pending
                onActivate={() => setAssociateFor(u)}
                onDelete={() => setDeleteFor(u)}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Active users */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold mb-2">
          {t("admin.user_status_active")}
          <span className="ml-2 text-slate-400 font-normal normal-case tracking-normal">· {active.length}</span>
        </h2>
        <ul className="space-y-2" data-testid="admin-users-active-list">
          {active.map((u) => (
            <UserRow
              key={u.id || u._id}
              user={u}
              onDelete={() => setDeleteFor(u)}
            />
          ))}
        </ul>
      </section>

      {associateFor && (
        <AssociateDialog
          user={associateFor}
          dealers={dealers}
          onClose={() => setAssociateFor(null)}
          onDone={() => { setAssociateFor(null); load(); }}
        />
      )}
      {deleteFor && (
        <DeleteUserDialog
          user={deleteFor}
          onClose={() => setDeleteFor(null)}
          onDone={() => { setDeleteFor(null); load(); }}
        />
      )}
      {showPurge && (
        <PurgeTestDataDialog onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); load(); }} />
      )}
    </div>
  );
}

function UserRow({ user, pending = false, onActivate, onDelete }) {
  const { t } = useTranslation();
  const protectedEmails = new Set(["admin@autexio.com", "demo@autexio.com", "demo-de@autexio.com"]);
  const isProtected = protectedEmails.has((user.email || "").toLowerCase()) || (user.role || "").toLowerCase() === "admin";
  const emailVerified = user.email_verified !== false;
  return (
    <li
      data-testid={`admin-user-row-${user.id || user._id}`}
      className="rounded-2xl bg-white border border-slate-100 p-4 flex items-center gap-3 flex-wrap"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        pending ? "bg-amber-50 text-amber-600 border border-amber-100" : "bg-slate-100 text-slate-500"
      }`}>
        {pending ? <Hourglass className="w-5 h-5" /> : <UserIcon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-sm">{user.name || user.email}</span>
          {pending ? (
            <span className="text-[9px] font-semibold uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
              {t("admin.user_status_pending")}
            </span>
          ) : (
            <span className="text-[9px] font-semibold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
              {t("admin.user_status_active")}
            </span>
          )}
          {user.is_demo && (
            <span
              data-testid={`admin-user-demo-chip-${user.id || user._id}`}
              title={t("admin.demo_chip_hint")}
              className="text-[9px] font-semibold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5"
            >
              {t("admin.demo_chip")}
            </span>
          )}
          {isProtected && (
            <span
              title="Protected account"
              className="text-[9px] font-semibold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
            >
              <ShieldCheck className="w-2.5 h-2.5" /> {(user.role || "user").toUpperCase()}
            </span>
          )}
          <span
            title={emailVerified ? t("admin.email_verified_yes") : t("admin.email_verified_no")}
            className={`text-[9px] font-semibold uppercase rounded px-1.5 py-0.5 border inline-flex items-center gap-1 ${
              emailVerified
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            {emailVerified ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
            {emailVerified ? t("admin.email_verified_short_yes") : t("admin.email_verified_short_no")}
          </span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
          <span>{user.email}</span>
          {user.signup_company_name && <span>· {user.signup_company_name}</span>}
          {user.signup_phone && <span>· {user.signup_phone}</span>}
          <span>· {(user.created_at || "").slice(0, 10)}</span>
        </div>
      </div>
      {pending && onActivate && (
        <Button
          onClick={onActivate}
          data-testid={`admin-user-activate-btn-${user.id || user._id}`}
          className="h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4"
        >
          <BadgeCheck className="w-3.5 h-3.5 mr-1" />
          {t("admin.activate_associate")}
        </Button>
      )}
      {!isProtected && (
        <Button
          onClick={onDelete}
          variant="outline"
          data-testid={`admin-user-delete-btn-${user.id || user._id}`}
          className="h-9 rounded-full text-xs font-semibold px-4 border-red-200 text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" /> {t("admin.user_delete")}
        </Button>
      )}
    </li>
  );
}

function DeleteUserDialog({ user, onClose, onDone }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/admin/users/${user.id || user._id}`);
      toast.success(t("admin.user_delete_ok"));
      onDone();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "protected_account") setErr(t("admin.user_delete_protected"));
      else setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-user-delete-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-100">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-red-700 flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> {t("admin.user_delete_title")}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          {t("admin.user_delete_body", { email: user.email })}
        </p>
        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="admin-user-delete-confirm"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.user_delete_confirm_btn")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PurgeTestDataDialog({ onClose, onDone }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const runPreview = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/admin/purge-test-data", { confirm: "PURGE", dry_run: true });
      setPreview(r.data.counts || null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const runPurge = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/admin/purge-test-data", { confirm: "PURGE", dry_run: false });
      const c = r.data.deleted || {};
      toast.success(t("admin.purge_test_ok", { users: c.users || 0, dealers: c.dealers || 0, requests: c.requests || 0 }));
      onDone();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-purge-dialog">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl border border-slate-100">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-red-700 flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> {t("admin.purge_test_title")}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          {t("admin.purge_test_body")}
        </p>
        {preview && (
          <div
            data-testid="admin-purge-preview"
            className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-700"
          >
            {t("admin.purge_test_preview", {
              users: preview.users || 0,
              dealers: preview.dealers || 0,
              requests: preview.requests || 0,
              pratiche: preview.pratiche || 0,
            })}
          </div>
        )}
        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
        <div className="mt-5 flex gap-2 justify-end flex-wrap">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={busy}
            data-testid="admin-purge-dry-run"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.purge_test_dry_run")}
          </Button>
          <Button
            onClick={runPurge}
            disabled={busy || !preview}
            data-testid="admin-purge-confirm"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.purge_test_confirm_btn")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AdminVehicles() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const params = { limit: 60, ...demoParams() };
    if (q) params.q = q;
    api.get("/admin/vehicles", { params }).then((r) => setItems(r.data.items || r.data || []));
  }, [q, demoParams]);
  if (items === null) return <SpinnerRow />;
  return (
    <div className="space-y-4" data-testid="admin-vehicles-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">{t("admin.vehicles_title")}</h1>
        <div className="relative w-full max-w-xs">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.search_placeholder")} className="pl-8 h-10" />
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold text-xs uppercase">{t("admin.col.vehicle")}</th>
              <th className="text-left px-4 py-2 font-semibold text-xs uppercase">{t("admin.col.country")}</th>
              <th className="text-right px-4 py-2 font-semibold text-xs uppercase">{t("admin.col.price")}</th>
              <th className="text-left px-4 py-2 font-semibold text-xs uppercase">{t("admin.col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 60).map((v) => (
              <tr key={v.id} className="border-t border-slate-100">
                <td className="px-4 py-2">
                  {v.brand} {v.model} <span className="text-slate-400 text-xs">· {v.year}</span>
                  {v.is_demo && (
                    <span
                      title={t("admin.demo_chip_hint")}
                      className="ml-2 text-[9px] font-semibold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5"
                    >
                      {t("admin.demo_chip")}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">{countryFlag(v.country)} {v.country}</td>
                <td className="px-4 py-2 text-right tabular-nums">{formatEUR(v.price_eur)}</td>
                <td className="px-4 py-2">{v.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminCommissions() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard", { params: demoParams() }).then((r) => setData(r.data));
  }, [demoParams]);
  if (!data) return <SpinnerRow />;
  return (
    <div className="space-y-6" data-testid="admin-commissions-page">
      <h1 className="font-heading font-bold text-3xl tracking-tighter">{t("admin.commissions_title")}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard testid="comm-month" title={t("admin.commissions_month")} value={formatEUR(data.commissions?.month_eur || 0)} Icon={Euro} highlight />
        <StatCard testid="comm-all" title={t("admin.commissions_all")} value={formatEUR(data.commissions?.all_time_eur || 0)} Icon={Receipt} />
      </div>
      <p className="text-sm text-slate-500">{t("admin.commissions_note")}</p>
    </div>
  );
}

function AdminActivity() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    api.get("/admin/activity", { params: filter ? { action: filter, limit: 200, ...demoParams() } : { limit: 200, ...demoParams() } })
      .then((r) => setItems(r.data || []));
  }, [filter, demoParams]);
  if (items === null) return <SpinnerRow />;
  return (
    <div className="space-y-4" data-testid="admin-activity-page">
      <h1 className="font-heading font-bold text-3xl tracking-tighter">{t("admin.activity_title")}</h1>
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-slate-400" />
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("admin.filter.action_placeholder")} className="max-w-xs" data-testid="admin-activity-filter" />
      </div>
      <ul className="space-y-2" data-testid="admin-activity-list">
        {items.map((ev, i) => (
          <li key={i} className="rounded-xl border border-slate-100 bg-white p-3">
            <div className="text-sm font-medium">{ev.action}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {ev.user_email || ev.user_id} · {new Date(ev.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdminRoadmap() {
  const { t } = useTranslation();
  const items = [
    "dms_api", "csv_import", "catalog_sync", "online_payments",
    "digital_signature", "push_notifications", "multi_user", "custom_pricing", "external_feeds",
  ];
  return (
    <div className="space-y-6" data-testid="admin-roadmap-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{t("admin.roadmap_overline")}</p>
        <h1 className="mt-1 font-heading font-bold text-3xl tracking-tighter">{t("admin.roadmap_title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("admin.roadmap_subtitle")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((k) => (
          <div key={k} className="rounded-2xl bg-white border border-slate-100 p-4">
            <div className="text-[10px] uppercase tracking-wider text-blue-600 font-semibold">{t("admin.upcoming")}</div>
            <div className="mt-1 font-heading font-semibold text-sm">{t(`admin.roadmap_items.${k}.title`)}</div>
            <div className="text-xs text-slate-500 mt-1">{t(`admin.roadmap_items.${k}.desc`)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaceholderR3() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-10 text-center" data-testid="admin-placeholder-r3">
      <Sparkles className="w-6 h-6 text-blue-500 mx-auto" />
      <h2 className="mt-3 font-heading font-semibold text-lg">{t("admin.placeholder_r3_title")}</h2>
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{t("admin.placeholder_r3_desc")}</p>
    </div>
  );
}

// ─────────────────────────── shared bits ────────────────────────────
function Panel({ title, children, testid, span2 = false }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl bg-white border border-slate-100 shadow-sm p-5 ${span2 ? "lg:col-span-2" : ""}`}
    >
      <h3 className="font-heading font-semibold text-base tracking-tight text-slate-950 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function SpinnerRow() {
  return (
    <div className="py-12 flex justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
    </div>
  );
}

function EmptyBox({ text }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}

// ─────────────────────────── STOCK FEEDS (admin) ─────────────────────────
function AdminStockFeeds() {
  const { t } = useTranslation();
  const { demoParams } = useAdminDemo();
  const [feeds, setFeeds] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState("");
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  const reload = () => {
    api
      .get("/admin/stock-feeds", { params: demoParams() })
      .then((r) => setFeeds(r.data?.items || []))
      .catch(() => setFeeds([]));
  };

  useEffect(() => {
    reload();
    api
      .get("/dealers")
      .then((r) => setDealers(r.data?.items || r.data || []))
      .catch(() => setDealers([]));
  }, [demoParams]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-stock-feeds-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("admin.stock_feeds.overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl text-slate-950">
            {t("admin.stock_feeds.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("admin.stock_feeds.subtitle")}</p>
        </div>
      </div>

      {feeds == null ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", "Caricamento…")}
        </div>
      ) : feeds.length === 0 ? (
        <EmptyBox text={t("admin.stock_feeds.empty")} />
      ) : (
        <div className="space-y-4">
          {feeds.map((f) => (
            <div
              key={f.id}
              data-testid={`admin-feed-row-${f.id}`}
              className="rounded-2xl border border-slate-100 bg-white p-5"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                    {f.dealer?.country ? countryFlag(f.dealer.country) : ""} {f.dealer?.name || f.dealer_id}
                  </div>
                  <div className="text-sm font-heading font-semibold tracking-tight truncate">
                    {f.url}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {f.format?.toUpperCase()} · {t("stock_feed.every")} {f.interval_hours}h
                    {f.simulated && (
                      <span className="ml-2 text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                        {t("stock_feed.simulated_badge")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {t("stock_feed.last_result")}
                  </div>
                  <div className="text-xs">
                    <span className="text-emerald-600 font-semibold">+{f.last_result?.added ?? 0}</span>
                    {" · "}
                    <span className="text-blue-600 font-semibold">↻{f.last_result?.updated ?? 0}</span>
                    {" · "}
                    <span className="text-slate-600 font-semibold">−{f.last_result?.marked_unavailable ?? 0}</span>
                    {" · "}
                    <span className={f.last_result?.errors?.length ? "text-red-600 font-semibold" : "text-slate-400"}>
                      !{f.last_result?.errors?.length ?? 0}
                    </span>
                  </div>
                </div>
              </div>
              <StockFeedStatusCard
                scope="admin"
                dealerId={f.dealer_id}
                feed={f}
                onSynced={reload}
              />
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-dashed border-slate-200 p-5">
        <h2 className="font-heading font-semibold text-base tracking-tight text-slate-950 mb-3">
          {t("admin.stock_feeds.connect_for_dealer")}
        </h2>
        <div className="flex items-center gap-2 mb-4">
          <select
            data-testid="admin-stock-feeds-dealer-select"
            value={selectedDealerId}
            onChange={(e) => setSelectedDealerId(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white h-10 px-3 text-sm min-w-[300px]"
          >
            <option value="">{t("admin.stock_feeds.select_dealer")}</option>
            {dealers.map((d) => (
              <option key={d.id} value={d.id}>
                {countryFlag(d.country)} {d.name}
              </option>
            ))}
          </select>
        </div>
        {selectedDealerId && (
          <StockFeedPanel
            key={selectedDealerId}
            scope="admin"
            dealerId={selectedDealerId}
            demoBackendBase={backendBase}
            onChanged={reload}
          />
        )}
      </div>
    </div>
  );
}

